// ---------------------------------------------------------------------------
// home.content.ts — Translated content for ForHotelsPage (homepage)
// ---------------------------------------------------------------------------
import type { SupportedLocale, LocaleContentMap } from '../i18n.config';

export interface HomeContent {
  hero: {
    title: string;
    subtitle: string;
    ctaStart: string;
    ctaDemo: string;
  };
  features: {
    title: string;
    subtitle: string;
    items: Array<{ title: string; description: string }>;
  };
  stats: Array<{ value: string; label: string }>;
  benefits: string[];
  whyChoose: { title: string; subtitle: string };
  portal: {
    badge: string;
    title: string;
    subtitle: string;
    subtitleLink: string;
    howItWorks: string;
    howItWorksDesc: string;
    visitBtn: string;
    items: Array<{ title: string; desc: string }>;
  };
  cta: {
    title: string;
    subtitle: string;
    trialBtn: string;
    contactBtn: string;
    disclaimer: string;
  };
}

const en: HomeContent = {
  hero: {
    title: 'AI-Powered All-in-One Hotel Management Software Built for Modern Hotels',
    subtitle: 'Cloud-based hotel PMS with 25 built-in AI features — dynamic pricing, AI guest concierge, predictive maintenance, sentiment analysis, and demand forecasting — running on the same platform as your booking engine, channel manager, and guest CRM. Trusted by 500+ properties worldwide.',
    ctaStart: 'Get Started Free',
    ctaDemo: 'Schedule Demo',
  },
  features: {
    title: 'AI-First Hotel Operations',
    subtitle: 'Production-ready AI plus the full hotel stack — one platform, one bill, one login',
    items: [
      { title: 'AI Dynamic Pricing', description: 'Demand-aware rate recommendations driven by occupancy, competitor rates, and historical patterns.' },
      { title: 'AI Guest Concierge', description: '24/7 multilingual chat concierge that answers guest questions, suggests upsells, and escalates to staff when needed.' },
      { title: 'AI Review Responses', description: 'Auto-generates on-brand management replies to OTA reviews with one-click send. Learns from your tone.' },
      { title: 'AI Demand Forecasting', description: '30/60/90-day occupancy and revenue forecasts with confidence intervals for revenue planning.' },
      { title: 'AI Sentiment Analysis', description: 'Tracks guest sentiment across reviews, surveys, and chat to surface issues before they hit TripAdvisor.' },
      { title: 'Predictive Maintenance', description: 'Ticket history models flag room equipment likely to fail before the next check-in.' },
      { title: 'Channel Manager + PMS + CRM', description: '40+ integrated modules — front desk, reservations, housekeeping, billing, group sales, and F&B — behind one login.' },
    ],
  },
  stats: [
    { value: '500+', label: 'Hotels Onboarded' },
    { value: '40+', label: 'Feature Modules' },
    { value: '99.9%', label: 'Uptime SLA' },
    { value: '24/7', label: 'Support Available' },
  ],
  benefits: [
    'Reduce manual work by 70% with automation',
    'Increase direct bookings by 40%',
    'Real-time multi-property oversight for chains',
    'Dedicated account manager for Enterprise plans',
    'Free data migration from your existing PMS',
  ],
  whyChoose: {
    title: 'Why Choose HotelStack.io',
    subtitle: 'Proven results for properties of every size',
  },
  portal: {
    badge: 'Guest Booking Portal',
    title: 'Your Guests Book on BookMyRoom.io',
    subtitle: 'Every property on HotelStack.io gets a dedicated listing on',
    subtitleLink: '— our guest-facing booking portal with search, reviews, and instant reservations.',
    howItWorks: 'How it works',
    howItWorksDesc: 'You manage on HotelStack.io → Guests discover and book on BookMyRoom.io → Reservations sync instantly to your PMS',
    visitBtn: 'Visit BookMyRoom.io',
    items: [
      { title: 'Hotel Discovery', desc: 'Guests search by location, price, rating, and amenities with real-time availability' },
      { title: 'Mobile-First Booking', desc: 'Responsive booking experience optimized for every device and screen size' },
      { title: 'Verified Reviews', desc: 'Authentic guest reviews and ratings build trust and drive conversions' },
      { title: 'Instant Confirmation', desc: 'Bookings sync in real-time to your HotelStack.io PMS dashboard' },
    ],
  },
  cta: {
    title: 'Ready to Transform Your Hotel Operations?',
    subtitle: 'Join 500+ hotels using HotelStack.io to streamline operations, boost revenue, and deliver exceptional guest experiences.',
    trialBtn: 'Start Free Trial',
    contactBtn: 'Contact Sales',
    disclaimer: 'No credit card required. 14-day free trial with full access.',
  },
};

const hi: HomeContent = {
  hero: {
    title: 'होटल और रिसॉर्ट के लिए ऑल-इन-वन होटल प्रबंधन सॉफ्टवेयर',
    subtitle: '25 AI फीचर्स, बुकिंग इंजन, चैनल मैनेजर, गेस्ट CRM और रेवेन्यू इंटेलिजेंस के साथ क्लाउड-आधारित होटल PMS — AI-पावर्ड प्राइसिंग, गेस्ट कंसीयज, और प्रेडिक्टिव ऑपरेशन्स। दुनिया भर में 500+ संपत्तियों द्वारा विश्वसनीय',
    ctaStart: 'मुफ़्त शुरू करें',
    ctaDemo: 'डेमो शेड्यूल करें',
  },
  features: {
    title: 'अपना होटल चलाने के लिए सब कुछ',
    subtitle: 'आधुनिक आतिथ्य संचालन के लिए डिज़ाइन किया गया व्यापक उपकरण सूट',
    items: [
      { title: 'प्रॉपर्टी प्रबंधन', description: 'एक डैशबोर्ड में पूर्ण फ्रंट डेस्क, आरक्षण, हाउसकीपिंग और कमरा प्रबंधन' },
      { title: 'रेवेन्यू इंटेलिजेंस', description: 'डायनामिक प्राइसिंग और व्हाट-इफ सीनारियो के साथ रीयल-टाइम RevPAR, ADR, ऑक्यूपेंसी एनालिटिक्स' },
      { title: 'चैनल मैनेजर', description: 'Booking.com, Expedia, Agoda सहित 100+ OTAs में दरें और उपलब्धता सिंक करें' },
      { title: 'गेस्ट CRM', description: 'लॉयल्टी प्रोग्राम, प्राथमिकता ट्रैकिंग और संचार उपकरण के साथ 360-डिग्री गेस्ट प्रोफ़ाइल' },
      { title: 'बिलिंग और भुगतान', description: 'स्वचालित चालान, बहु-मुद्रा समर्थन, कर अनुपालन और एकीकृत भुगतान गेटवे' },
      { title: 'एंटरप्राइज़ सुरक्षा', description: 'भूमिका-आधारित पहुंच, ऑडिट ट्रेल, डेटा एन्क्रिप्शन और क्षेत्रीय नियामक अनुपालन' },
    ],
  },
  stats: [
    { value: '500+', label: 'होटल ऑनबोर्डेड' },
    { value: '40+', label: 'फीचर मॉड्यूल' },
    { value: '99.9%', label: 'अपटाइम SLA' },
    { value: '24/7', label: 'सहायता उपलब्ध' },
  ],
  benefits: [
    'स्वचालन से मैन्युअल काम में 70% कमी',
    'प्रत्यक्ष बुकिंग में 40% वृद्धि',
    'चेन के लिए रीयल-टाइम मल्टी-प्रॉपर्टी ओवरसाइट',
    'एंटरप्राइज़ योजनाओं के लिए समर्पित अकाउंट मैनेजर',
    'मौजूदा PMS से मुफ़्त डेटा माइग्रेशन',
  ],
  whyChoose: {
    title: 'HotelStack.io क्यों चुनें',
    subtitle: 'हर आकार की संपत्तियों के लिए सिद्ध परिणाम',
  },
  portal: {
    badge: 'गेस्ट बुकिंग पोर्टल',
    title: 'आपके अतिथि BookMyRoom.io पर बुक करते हैं',
    subtitle: 'HotelStack.io पर हर संपत्ति को एक समर्पित लिस्टिंग मिलती है',
    subtitleLink: '— हमारे गेस्ट-फेसिंग बुकिंग पोर्टल पर खोज, समीक्षा और तत्काल आरक्षण के साथ।',
    howItWorks: 'यह कैसे काम करता है',
    howItWorksDesc: 'आप HotelStack.io पर प्रबंधित करते हैं → अतिथि BookMyRoom.io पर खोजते हैं और बुक करते हैं → आरक्षण तुरंत आपके PMS से सिंक होते हैं',
    visitBtn: 'BookMyRoom.io पर जाएं',
    items: [
      { title: 'होटल खोज', desc: 'अतिथि रीयल-टाइम उपलब्धता के साथ स्थान, मूल्य, रेटिंग और सुविधाओं के अनुसार खोजते हैं' },
      { title: 'मोबाइल-फर्स्ट बुकिंग', desc: 'हर डिवाइस और स्क्रीन साइज़ के लिए अनुकूलित रिस्पॉन्सिव बुकिंग अनुभव' },
      { title: 'सत्यापित समीक्षाएं', desc: 'प्रामाणिक अतिथि समीक्षा और रेटिंग भरोसा बनाती हैं और रूपांतरण बढ़ाती हैं' },
      { title: 'तत्काल पुष्टिकरण', desc: 'बुकिंग रीयल-टाइम में आपके HotelStack.io PMS डैशबोर्ड से सिंक होती हैं' },
    ],
  },
  cta: {
    title: 'अपने होटल संचालन को बदलने के लिए तैयार हैं?',
    subtitle: '500+ होटलों से जुड़ें जो HotelStack.io का उपयोग संचालन को सुव्यवस्थित करने, राजस्व बढ़ाने और असाधारण अतिथि अनुभव प्रदान करने के लिए करते हैं।',
    trialBtn: 'मुफ़्त ट्रायल शुरू करें',
    contactBtn: 'बिक्री से संपर्क करें',
    disclaimer: 'क्रेडिट कार्ड की आवश्यकता नहीं। पूर्ण पहुंच के साथ 14 दिन का मुफ़्त ट्रायल।',
  },
};

export const HOME_CONTENT: LocaleContentMap<HomeContent> = {
  en,
  ar: {
    hero: {
      title: 'برنامج إدارة فنادق متكامل للفنادق والمنتجعات',
      subtitle: 'نظام إدارة فنادق سحابي مع 25 ميزة ذكاء اصطناعي ومحرك حجز ومدير قنوات وإدارة علاقات النزلاء — تسعير AI وكونسيرج ضيوف وعمليات تنبؤية. موثوق من أكثر من ٥٠٠ فندق حول العالم',
      ctaStart: 'ابدأ مجاناً',
      ctaDemo: 'جدولة عرض توضيحي',
    },
    features: {
      title: 'كل ما تحتاجه لإدارة فندقك',
      subtitle: 'مجموعة شاملة من الأدوات المصممة لعمليات الضيافة الحديثة',
      items: [
        { title: 'إدارة الممتلكات', description: 'مكتب استقبال كامل والحجوزات والتدبير المنزلي وإدارة الغرف في لوحة تحكم واحدة' },
        { title: 'ذكاء الإيرادات', description: 'تحليلات RevPAR وADR والإشغال في الوقت الفعلي مع التسعير الديناميكي وسيناريوهات ماذا لو' },
        { title: 'مدير القنوات', description: 'مزامنة الأسعار والتوافر عبر أكثر من ١٠٠ وكالة سفر عبر الإنترنت' },
        { title: 'إدارة علاقات الضيوف', description: 'ملفات تعريف ضيوف شاملة مع برامج الولاء وتتبع التفضيلات وأدوات التواصل' },
        { title: 'الفوترة والمدفوعات', description: 'فواتير آلية ودعم العملات المتعددة والامتثال الضريبي وبوابات دفع متكاملة' },
        { title: 'أمان المؤسسات', description: 'تحكم بالوصول حسب الأدوار وسجلات التدقيق وتشفير البيانات والامتثال التنظيمي' },
      ],
    },
    stats: [
      { value: '+٥٠٠', label: 'فندق مسجل' },
      { value: '٣٦', label: 'وحدة ميزات' },
      { value: '٩٩٫٩%', label: 'وقت التشغيل' },
      { value: '٢٤/٧', label: 'دعم متاح' },
    ],
    benefits: [
      'تقليل العمل اليدوي بنسبة ٧٠% مع الأتمتة',
      'زيادة الحجوزات المباشرة بنسبة ٤٠%',
      'رقابة متعددة العقارات في الوقت الفعلي للسلاسل',
      'مدير حساب مخصص لخطط المؤسسات',
      'نقل بيانات مجاني من نظام إدارة الفنادق الحالي',
    ],
    whyChoose: { title: 'لماذا تختار HotelStack.io', subtitle: 'نتائج مثبتة لعقارات بجميع الأحجام' },
    portal: {
      badge: 'بوابة حجز الضيوف',
      title: 'ضيوفك يحجزون على BookMyRoom.io',
      subtitle: 'كل عقار على HotelStack.io يحصل على قائمة مخصصة على',
      subtitleLink: '— بوابة الحجز للضيوف مع البحث والمراجعات والحجوزات الفورية.',
      howItWorks: 'كيف يعمل',
      howItWorksDesc: 'أنت تدير على HotelStack.io ← الضيوف يكتشفون ويحجزون على BookMyRoom.io ← الحجوزات تتزامن فوراً مع نظام PMS الخاص بك',
      visitBtn: 'زيارة BookMyRoom.io',
      items: [
        { title: 'اكتشاف الفنادق', desc: 'يبحث الضيوف حسب الموقع والسعر والتقييم والمرافق مع التوافر في الوقت الفعلي' },
        { title: 'حجز متوافق مع الجوال', desc: 'تجربة حجز متجاوبة محسنة لكل جهاز وحجم شاشة' },
        { title: 'تقييمات موثقة', desc: 'تقييمات ومراجعات حقيقية من الضيوف تبني الثقة وتزيد التحويلات' },
        { title: 'تأكيد فوري', desc: 'الحجوزات تتزامن في الوقت الفعلي مع لوحة تحكم HotelStack.io PMS' },
      ],
    },
    cta: {
      title: 'مستعد لتحويل عمليات فندقك؟',
      subtitle: 'انضم إلى أكثر من ٥٠٠ فندق يستخدمون HotelStack.io لتبسيط العمليات وزيادة الإيرادات وتقديم تجارب استثنائية للضيوف.',
      trialBtn: 'ابدأ تجربة مجانية',
      contactBtn: 'اتصل بالمبيعات',
      disclaimer: 'لا حاجة لبطاقة ائتمان. تجربة مجانية لمدة ١٤ يوماً مع وصول كامل.',
    },
  },
  es: {
    hero: {
      title: 'Software de Gestión Hotelera Todo en Uno para Hoteles y Resorts',
      subtitle: 'PMS hotelero en la nube con 25 funciones de IA, motor de reservas, gestor de canales, CRM de huéspedes e inteligencia de ingresos — pricing AI, concierge de huéspedes y operaciones predictivas. Confianza de más de 500 propiedades',
      ctaStart: 'Comenzar Gratis',
      ctaDemo: 'Agendar Demo',
    },
    features: {
      title: 'Todo lo Que Necesita para Administrar su Hotel',
      subtitle: 'Un conjunto integral de herramientas diseñadas para operaciones hoteleras modernas',
      items: [
        { title: 'Gestión de Propiedades', description: 'Recepción completa, reservas, limpieza y gestión de habitaciones en un solo panel' },
        { title: 'Inteligencia de Ingresos', description: 'Análisis en tiempo real de RevPAR, ADR, ocupación con precios dinámicos y escenarios hipotéticos' },
        { title: 'Gestor de Canales', description: 'Sincronice tarifas y disponibilidad en más de 100 OTAs incluyendo Booking.com, Expedia, Agoda' },
        { title: 'CRM de Huéspedes', description: 'Perfiles de huéspedes de 360 grados con programas de lealtad, seguimiento de preferencias y herramientas de comunicación' },
        { title: 'Facturación y Pagos', description: 'Facturación automatizada, soporte multi-moneda, cumplimiento fiscal y pasarelas de pago integradas' },
        { title: 'Seguridad Empresarial', description: 'Control de acceso por roles, registros de auditoría, cifrado de datos y cumplimiento regulatorio regional' },
      ],
    },
    stats: [
      { value: '500+', label: 'Hoteles Registrados' },
      { value: '40+', label: 'Módulos de Funciones' },
      { value: '99.9%', label: 'SLA de Disponibilidad' },
      { value: '24/7', label: 'Soporte Disponible' },
    ],
    benefits: [
      'Reduzca el trabajo manual en un 70% con automatización',
      'Aumente las reservas directas en un 40%',
      'Supervisión multi-propiedad en tiempo real para cadenas',
      'Gerente de cuenta dedicado para planes Enterprise',
      'Migración de datos gratuita desde su PMS actual',
    ],
    whyChoose: { title: '¿Por Qué Elegir HotelStack.io?', subtitle: 'Resultados comprobados para propiedades de todos los tamaños' },
    portal: {
      badge: 'Portal de Reservas para Huéspedes',
      title: 'Sus Huéspedes Reservan en BookMyRoom.io',
      subtitle: 'Cada propiedad en HotelStack.io obtiene un listado dedicado en',
      subtitleLink: '— nuestro portal de reservas para huéspedes con búsqueda, reseñas y reservas instantáneas.',
      howItWorks: 'Cómo funciona',
      howItWorksDesc: 'Usted administra en HotelStack.io → Los huéspedes descubren y reservan en BookMyRoom.io → Las reservas se sincronizan al instante con su PMS',
      visitBtn: 'Visitar BookMyRoom.io',
      items: [
        { title: 'Descubrimiento de Hoteles', desc: 'Los huéspedes buscan por ubicación, precio, calificación y servicios con disponibilidad en tiempo real' },
        { title: 'Reserva Mobile-First', desc: 'Experiencia de reserva responsiva optimizada para cada dispositivo y tamaño de pantalla' },
        { title: 'Reseñas Verificadas', desc: 'Reseñas y calificaciones auténticas de huéspedes que generan confianza y conversiones' },
        { title: 'Confirmación Instantánea', desc: 'Las reservas se sincronizan en tiempo real con el panel de HotelStack.io PMS' },
      ],
    },
    cta: {
      title: '¿Listo para Transformar las Operaciones de su Hotel?',
      subtitle: 'Únase a más de 500 hoteles que usan HotelStack.io para optimizar operaciones, aumentar ingresos y ofrecer experiencias excepcionales.',
      trialBtn: 'Iniciar Prueba Gratuita',
      contactBtn: 'Contactar Ventas',
      disclaimer: 'Sin tarjeta de crédito. Prueba gratuita de 14 días con acceso completo.',
    },
  },
  fr: {
    hero: {
      title: 'Logiciel de Gestion Hôtelière Tout-en-Un pour Hôtels et Resorts',
      subtitle: 'PMS hôtelier cloud avec 25 fonctions IA, moteur de réservation, gestionnaire de canaux, CRM clients et intelligence des revenus — tarification IA, concierge client et opérations prédictives. Plus de 500 établissements nous font confiance',
      ctaStart: 'Démarrer Gratuitement',
      ctaDemo: 'Planifier une Démo',
    },
    features: {
      title: 'Tout ce Dont Vous Avez Besoin pour Gérer Votre Hôtel',
      subtitle: 'Une suite complète d\'outils conçus pour les opérations hôtelières modernes',
      items: [
        { title: 'Gestion de Propriété', description: 'Réception, réservations, ménage et gestion des chambres dans un seul tableau de bord' },
        { title: 'Intelligence des Revenus', description: 'Analyses en temps réel RevPAR, ADR, occupation avec tarification dynamique et scénarios hypothétiques' },
        { title: 'Gestionnaire de Canaux', description: 'Synchronisez tarifs et disponibilités sur plus de 100 OTAs dont Booking.com, Expedia, Agoda' },
        { title: 'CRM Clients', description: 'Profils clients à 360 degrés avec programmes de fidélité, suivi des préférences et outils de communication' },
        { title: 'Facturation et Paiements', description: 'Facturation automatisée, support multi-devises, conformité fiscale et passerelles de paiement intégrées' },
        { title: 'Sécurité Entreprise', description: 'Contrôle d\'accès par rôles, pistes d\'audit, chiffrement des données et conformité réglementaire régionale' },
      ],
    },
    stats: [
      { value: '500+', label: 'Hôtels Inscrits' },
      { value: '40+', label: 'Modules Fonctionnels' },
      { value: '99.9%', label: 'SLA de Disponibilité' },
      { value: '24/7', label: 'Support Disponible' },
    ],
    benefits: [
      'Réduisez le travail manuel de 70% grâce à l\'automatisation',
      'Augmentez les réservations directes de 40%',
      'Surveillance multi-propriétés en temps réel pour les chaînes',
      'Gestionnaire de compte dédié pour les plans Enterprise',
      'Migration de données gratuite depuis votre PMS actuel',
    ],
    whyChoose: { title: 'Pourquoi Choisir HotelStack.io', subtitle: 'Des résultats prouvés pour des propriétés de toutes tailles' },
    portal: {
      badge: 'Portail de Réservation Clients',
      title: 'Vos Clients Réservent sur BookMyRoom.io',
      subtitle: 'Chaque propriété sur HotelStack.io obtient une fiche dédiée sur',
      subtitleLink: '— notre portail de réservation clients avec recherche, avis et réservations instantanées.',
      howItWorks: 'Comment ça marche',
      howItWorksDesc: 'Vous gérez sur HotelStack.io → Les clients découvrent et réservent sur BookMyRoom.io → Les réservations se synchronisent instantanément avec votre PMS',
      visitBtn: 'Visiter BookMyRoom.io',
      items: [
        { title: 'Découverte d\'Hôtels', desc: 'Les clients recherchent par emplacement, prix, note et équipements avec disponibilité en temps réel' },
        { title: 'Réservation Mobile-First', desc: 'Expérience de réservation responsive optimisée pour chaque appareil et taille d\'écran' },
        { title: 'Avis Vérifiés', desc: 'Avis et notes authentiques de clients qui renforcent la confiance et les conversions' },
        { title: 'Confirmation Instantanée', desc: 'Les réservations se synchronisent en temps réel avec le tableau de bord HotelStack.io PMS' },
      ],
    },
    cta: {
      title: 'Prêt à Transformer les Opérations de Votre Hôtel ?',
      subtitle: 'Rejoignez plus de 500 hôtels qui utilisent HotelStack.io pour optimiser les opérations, augmenter les revenus et offrir des expériences exceptionnelles.',
      trialBtn: 'Démarrer l\'Essai Gratuit',
      contactBtn: 'Contacter les Ventes',
      disclaimer: 'Sans carte de crédit. Essai gratuit de 14 jours avec accès complet.',
    },
  },
  de: {
    hero: {
      title: 'All-in-One Hotelmanagementsoftware für Hotels und Resorts',
      subtitle: 'Cloud-basiertes Hotel-PMS mit 25 KI-Funktionen, Buchungsmaschine, Channel Manager, Gäste-CRM und Revenue Intelligence — KI-Preisgestaltung, Gäste-Concierge und prädiktive Operationen. Über 500 Hotels vertrauen uns',
      ctaStart: 'Kostenlos Starten',
      ctaDemo: 'Demo Vereinbaren',
    },
    features: {
      title: 'Alles Was Sie für Ihr Hotel Brauchen',
      subtitle: 'Ein umfassendes Set an Tools für moderne Hotelbetriebe',
      items: [
        { title: 'Immobilienverwaltung', description: 'Komplette Rezeption, Reservierungen, Housekeeping und Zimmerverwaltung in einem Dashboard' },
        { title: 'Revenue Intelligence', description: 'Echtzeit RevPAR, ADR, Auslastungsanalysen mit dynamischer Preisgestaltung und Was-wäre-wenn-Szenarien' },
        { title: 'Channel Manager', description: 'Synchronisieren Sie Preise und Verfügbarkeit über 100+ OTAs inkl. Booking.com, Expedia, Agoda' },
        { title: 'Gäste-CRM', description: '360-Grad-Gästeprofile mit Treueprogrammen, Präferenzverfolgung und Kommunikationstools' },
        { title: 'Abrechnung und Zahlungen', description: 'Automatisierte Rechnungsstellung, Multi-Währungs-Support, Steuerkonformität und integrierte Zahlungsgateways' },
        { title: 'Enterprise-Sicherheit', description: 'Rollenbasierte Zugriffskontrolle, Audit-Trails, Datenverschlüsselung und regionale Compliance' },
      ],
    },
    stats: [
      { value: '500+', label: 'Hotels Registriert' },
      { value: '40+', label: 'Funktionsmodule' },
      { value: '99.9%', label: 'Verfügbarkeits-SLA' },
      { value: '24/7', label: 'Support Verfügbar' },
    ],
    benefits: [
      'Reduzieren Sie manuelle Arbeit um 70% durch Automatisierung',
      'Steigern Sie Direktbuchungen um 40%',
      'Echtzeit-Multi-Property-Übersicht für Ketten',
      'Dedizierter Account Manager für Enterprise-Pläne',
      'Kostenlose Datenmigration von Ihrem bestehenden PMS',
    ],
    whyChoose: { title: 'Warum HotelStack.io Wählen', subtitle: 'Bewiesene Ergebnisse für Unterkünfte jeder Größe' },
    portal: {
      badge: 'Gäste-Buchungsportal',
      title: 'Ihre Gäste Buchen auf BookMyRoom.io',
      subtitle: 'Jede Unterkunft auf HotelStack.io erhält einen dedizierten Eintrag auf',
      subtitleLink: '— unser Gäste-Buchungsportal mit Suche, Bewertungen und Sofortbuchungen.',
      howItWorks: 'So funktioniert es',
      howItWorksDesc: 'Sie verwalten auf HotelStack.io → Gäste entdecken und buchen auf BookMyRoom.io → Reservierungen werden sofort mit Ihrem PMS synchronisiert',
      visitBtn: 'BookMyRoom.io Besuchen',
      items: [
        { title: 'Hotelentdeckung', desc: 'Gäste suchen nach Standort, Preis, Bewertung und Ausstattung mit Echtzeit-Verfügbarkeit' },
        { title: 'Mobile-First Buchung', desc: 'Responsive Buchungserfahrung optimiert für jedes Gerät und jede Bildschirmgröße' },
        { title: 'Verifizierte Bewertungen', desc: 'Authentische Gästebewertungen und -noten die Vertrauen schaffen und Conversions steigern' },
        { title: 'Sofortige Bestätigung', desc: 'Buchungen werden in Echtzeit mit dem HotelStack.io PMS-Dashboard synchronisiert' },
      ],
    },
    cta: {
      title: 'Bereit Ihren Hotelbetrieb zu Transformieren?',
      subtitle: 'Schließen Sie sich über 500 Hotels an, die HotelStack.io nutzen, um Abläufe zu optimieren, Umsatz zu steigern und außergewöhnliche Gästeerlebnisse zu bieten.',
      trialBtn: 'Kostenlose Testversion Starten',
      contactBtn: 'Vertrieb Kontaktieren',
      disclaimer: 'Keine Kreditkarte erforderlich. 14-tägige kostenlose Testversion mit vollem Zugang.',
    },
  },
  th: {
    hero: {
      title: 'ซอฟต์แวร์บริหารจัดการโรงแรมครบวงจรสำหรับโรงแรมและรีสอร์ท',
      subtitle: 'ระบบ PMS บนคลาวด์พร้อม 25 ฟีเจอร์ AI ระบบจอง ตัวจัดการช่องทาง CRM และวิเคราะห์รายได้ — AI กำหนดราคา, Concierge แขก และปฏิบัติการเชิงคาดการณ์ ได้รับความไว้วางใจจากกว่า 500 โรงแรมทั่วโลก',
      ctaStart: 'เริ่มต้นฟรี',
      ctaDemo: 'นัดหมายสาธิต',
    },
    features: {
      title: 'ทุกสิ่งที่คุณต้องการในการบริหารโรงแรม',
      subtitle: 'ชุดเครื่องมือครบวงจรที่ออกแบบสำหรับการดำเนินงานโรงแรมสมัยใหม่',
      items: [
        { title: 'การจัดการทรัพย์สิน', description: 'แผนกต้อนรับ การจอง แม่บ้าน และการจัดการห้องพักทั้งหมดในแดชบอร์ดเดียว' },
        { title: 'ระบบวิเคราะห์รายได้', description: 'วิเคราะห์ RevPAR, ADR, อัตราการเข้าพักแบบเรียลไทม์พร้อมการกำหนดราคาแบบไดนามิก' },
        { title: 'ตัวจัดการช่องทาง', description: 'ซิงค์ราคาและความพร้อมผ่าน OTA กว่า 100 แห่งรวมถึง Booking.com, Expedia, Agoda' },
        { title: 'CRM ผู้เข้าพัก', description: 'โปรไฟล์ผู้เข้าพักแบบ 360 องศาพร้อมโปรแกรมสะสมคะแนน การติดตามความชอบ และเครื่องมือสื่อสาร' },
        { title: 'การเรียกเก็บเงินและการชำระเงิน', description: 'ออกใบแจ้งหนี้อัตโนมัติ รองรับหลายสกุลเงิน ภาษีอัตโนมัติ และเกตเวย์ชำระเงินครบวงจร' },
        { title: 'ความปลอดภัยระดับองค์กร', description: 'การควบคุมการเข้าถึงตามบทบาท บันทึกการตรวจสอบ การเข้ารหัสข้อมูล และการปฏิบัติตามกฎระเบียบ' },
      ],
    },
    stats: [
      { value: '500+', label: 'โรงแรมที่ลงทะเบียน' },
      { value: '40+', label: 'โมดูลฟีเจอร์' },
      { value: '99.9%', label: 'SLA เวลาทำงาน' },
      { value: '24/7', label: 'บริการสนับสนุน' },
    ],
    benefits: [
      'ลดงานแมนนวล 70% ด้วยระบบอัตโนมัติ',
      'เพิ่มการจองตรง 40%',
      'ดูแลหลายสาขาแบบเรียลไทม์สำหรับเชน',
      'ผู้จัดการบัญชีเฉพาะสำหรับแผน Enterprise',
      'ย้ายข้อมูลฟรีจากระบบ PMS เดิมของคุณ',
    ],
    whyChoose: { title: 'ทำไมต้องเลือก HotelStack.io', subtitle: 'ผลลัพธ์ที่พิสูจน์แล้วสำหรับที่พักทุกขนาด' },
    portal: {
      badge: 'พอร์ทัลจองสำหรับผู้เข้าพัก',
      title: 'ผู้เข้าพักจองบน BookMyRoom.io',
      subtitle: 'ทุกที่พักบน HotelStack.io จะได้รับรายการเฉพาะบน',
      subtitleLink: '— พอร์ทัลจองสำหรับผู้เข้าพักพร้อมการค้นหา รีวิว และการจองทันที',
      howItWorks: 'วิธีการทำงาน',
      howItWorksDesc: 'คุณจัดการบน HotelStack.io → ผู้เข้าพักค้นพบและจองบน BookMyRoom.io → การจองซิงค์ทันทีกับ PMS ของคุณ',
      visitBtn: 'เยี่ยมชม BookMyRoom.io',
      items: [
        { title: 'ค้นหาโรงแรม', desc: 'ผู้เข้าพักค้นหาตามสถานที่ ราคา คะแนน และสิ่งอำนวยความสะดวกพร้อมความพร้อมแบบเรียลไทม์' },
        { title: 'จองแบบ Mobile-First', desc: 'ประสบการณ์การจองที่ตอบสนองได้รับการปรับให้เหมาะกับทุกอุปกรณ์และขนาดหน้าจอ' },
        { title: 'รีวิวที่ยืนยันแล้ว', desc: 'รีวิวและคะแนนจากผู้เข้าพักจริงที่สร้างความไว้วางใจและเพิ่มอัตราการแปลง' },
        { title: 'ยืนยันทันที', desc: 'การจองซิงค์แบบเรียลไทม์กับแดชบอร์ด HotelStack.io PMS' },
      ],
    },
    cta: {
      title: 'พร้อมที่จะเปลี่ยนแปลงการดำเนินงานโรงแรมของคุณ?',
      subtitle: 'เข้าร่วมกว่า 500 โรงแรมที่ใช้ HotelStack.io เพื่อปรับปรุงการดำเนินงาน เพิ่มรายได้ และมอบประสบการณ์ที่ยอดเยี่ยมแก่ผู้เข้าพัก',
      trialBtn: 'เริ่มทดลองใช้ฟรี',
      contactBtn: 'ติดต่อฝ่ายขาย',
      disclaimer: 'ไม่ต้องใช้บัตรเครดิต ทดลองใช้ฟรี 14 วันพร้อมสิทธิ์เข้าถึงเต็มรูปแบบ',
    },
  },
  ja: {
    hero: {
      title: 'ホテル＆リゾート向けオールインワンホテル管理ソフトウェア',
      subtitle: '25のAI機能を搭載したクラウドベースのホテルPMS、予約エンジン、チャネルマネージャー、ゲストCRM、収益インテリジェンス — AIダイナミックプライシング、ゲストコンシェルジュ、予測的オペレーション。世界中の500以上の施設に信頼されています',
      ctaStart: '無料で始める',
      ctaDemo: 'デモを予約',
    },
    features: {
      title: 'ホテル運営に必要なすべて',
      subtitle: '現代のホスピタリティ運営のために設計された包括的なツールスイート',
      items: [
        { title: 'プロパティ管理', description: 'フロントデスク、予約、ハウスキーピング、客室管理をひとつのダッシュボードで完結' },
        { title: '収益インテリジェンス', description: 'リアルタイムRevPAR、ADR、稼働率分析とダイナミックプライシング、シミュレーション機能' },
        { title: 'チャネルマネージャー', description: 'Booking.com、Expedia、Agodaなど100以上のOTAで料金と空室を同期' },
        { title: 'ゲストCRM', description: 'ロイヤルティプログラム、嗜好追跡、コミュニケーションツール付き360度ゲストプロフィール' },
        { title: '請求と決済', description: '自動請求書発行、多通貨対応、税務コンプライアンス、統合決済ゲートウェイ' },
        { title: 'エンタープライズセキュリティ', description: 'ロールベースのアクセス制御、監査証跡、データ暗号化、地域規制対応' },
      ],
    },
    stats: [
      { value: '500+', label: '登録ホテル' },
      { value: '40+', label: '機能モジュール' },
      { value: '99.9%', label: '稼働率SLA' },
      { value: '24/7', label: 'サポート対応' },
    ],
    benefits: [
      '自動化で手作業を70%削減',
      '直接予約を40%増加',
      'チェーン向けリアルタイムマルチプロパティ監視',
      'Enterpriseプラン向け専任アカウントマネージャー',
      '既存PMSからの無料データ移行',
    ],
    whyChoose: { title: 'HotelStack.ioを選ぶ理由', subtitle: 'あらゆる規模の施設に実証済みの結果' },
    portal: {
      badge: 'ゲスト予約ポータル',
      title: 'ゲストはBookMyRoom.ioで予約',
      subtitle: 'HotelStack.ioのすべての施設は',
      subtitleLink: 'に専用リスティングを取得 — 検索、レビュー、即時予約が可能なゲスト向け予約ポータル。',
      howItWorks: '仕組み',
      howItWorksDesc: 'あなたがHotelStack.ioで管理 → ゲストがBookMyRoom.ioで発見・予約 → 予約が即座にPMSに同期',
      visitBtn: 'BookMyRoom.ioを訪問',
      items: [
        { title: 'ホテル検索', desc: 'ゲストは場所、価格、評価、アメニティでリアルタイム空室状況を検索' },
        { title: 'モバイルファースト予約', desc: 'あらゆるデバイスと画面サイズに最適化されたレスポンシブ予約体験' },
        { title: '認証済みレビュー', desc: '信頼を築きコンバージョンを促進する本物のゲストレビューと評価' },
        { title: '即時確認', desc: '予約はHotelStack.io PMSダッシュボードとリアルタイムで同期' },
      ],
    },
    cta: {
      title: 'ホテル運営を変革する準備はできましたか？',
      subtitle: '500以上のホテルがHotelStack.ioを使用して運営を効率化し、収益を増やし、卓越したゲスト体験を提供しています。',
      trialBtn: '無料トライアルを開始',
      contactBtn: '営業に問い合わせ',
      disclaimer: 'クレジットカード不要。14日間の無料トライアルでフルアクセス。',
    },
  },
  zh: {
    hero: {
      title: '酒店和度假村一体化酒店管理软件',
      subtitle: '云端酒店PMS，集成25项AI功能、预订引擎、渠道管理器、客户CRM和收益智能 — AI动态定价、宾客礼宾和预测性运营。全球500多家酒店的信赖之选',
      ctaStart: '免费开始',
      ctaDemo: '预约演示',
    },
    features: {
      title: '经营酒店所需的一切',
      subtitle: '为现代酒店运营设计的综合工具套件',
      items: [
        { title: '物业管理', description: '在一个仪表板中完成前台、预订、客房清洁和房间管理' },
        { title: '收益智能', description: '实时RevPAR、ADR、入住率分析，动态定价和假设分析' },
        { title: '渠道管理器', description: '在100多个OTA上同步房价和房态，包括Booking.com、Expedia、Agoda' },
        { title: '客户CRM', description: '360度客户档案，忠诚度计划、偏好追踪和沟通工具' },
        { title: '账单和支付', description: '自动开票、多币种支持、税务合规和集成支付网关' },
        { title: '企业安全', description: '基于角色的访问控制、审计跟踪、数据加密和区域法规合规' },
      ],
    },
    stats: [
      { value: '500+', label: '注册酒店' },
      { value: '40+', label: '功能模块' },
      { value: '99.9%', label: '可用性SLA' },
      { value: '24/7', label: '支持服务' },
    ],
    benefits: [
      '通过自动化减少70%的手动工作',
      '直接预订增加40%',
      '连锁酒店实时多物业监控',
      'Enterprise计划专属客户经理',
      '从现有PMS免费数据迁移',
    ],
    whyChoose: { title: '为什么选择HotelStack.io', subtitle: '各种规模酒店的实证成果' },
    portal: {
      badge: '客人预订门户',
      title: '您的客人在BookMyRoom.io上预订',
      subtitle: 'HotelStack.io上的每个物业都在',
      subtitleLink: '获得专属列表 — 我们面向客人的预订门户，提供搜索、评论和即时预订。',
      howItWorks: '工作原理',
      howItWorksDesc: '您在HotelStack.io上管理 → 客人在BookMyRoom.io上发现和预订 → 预订即时同步到您的PMS',
      visitBtn: '访问BookMyRoom.io',
      items: [
        { title: '酒店发现', desc: '客人按位置、价格、评分和设施搜索，实时显示可用性' },
        { title: '移动优先预订', desc: '针对每个设备和屏幕尺寸优化的响应式预订体验' },
        { title: '已验证评论', desc: '真实的客人评论和评分，建立信任并提高转化率' },
        { title: '即时确认', desc: '预订实时同步到HotelStack.io PMS仪表板' },
      ],
    },
    cta: {
      title: '准备好改变您的酒店运营了吗？',
      subtitle: '加入500多家使用HotelStack.io的酒店，优化运营、提升收入并提供卓越的客人体验。',
      trialBtn: '开始免费试用',
      contactBtn: '联系销售',
      disclaimer: '无需信用卡。14天免费试用，完整访问权限。',
    },
  },
  hi,
};
